import KeyboardKeyIcon from '@/components/ui/keyboard-key-icon';
import HotKeyListener from '@/components/ux/hot-key-listener';
import { toast } from '@/hooks/use-toast';
import { HOTKEYS } from '@/lib/hotkeys';
import { pdkAxios } from '@/lib/pdk-axios';
import { cn } from '@/lib/utils';
import { vehicleListingStore } from '@/models/stores/vehicle-listings';
import type { VehicleListing } from '@/models/types';
import { useMutation } from '@tanstack/react-query';
import { useMemo, useRef } from 'react';
import {
  ComboboxPopover,
  type Option,
} from '../../ui/combobox/combobox-popover';
import { VehiclePreviousOwnersBadge } from './previous-owners-badge';

export const getPreviousOwnersOptions = (): Option<string>[] => {
  return [
    {
      value: '1',
      label: '1 previous owner',
      hotkey: '1',
    },
    {
      value: '2',
      label: '2 previous owners',
      hotkey: '2',
    },
    {
      value: '3',
      label: '3 previous owners',
      hotkey: '3',
    },
    {
      value: '4',
      label: '4 previous owners',
      hotkey: '4',
    },
    {
      value: '5',
      label: '5 previous owners',
      hotkey: '5',
    },
  ];
};

export const PREVIOUS_OWNERS_OPTIONS_MAP = getPreviousOwnersOptions();

export type VehiclePreviousOwnersSelectorProps = {
  id: string;
  value: VehicleListing['previous_owners'];
  hideCopy?: boolean;
  side?: 'left' | 'right' | 'bottom' | 'top';
  withHotkeyListener?: boolean;
  buttonVariant?: 'outline' | 'ghost';
  className?: string;
};

export function VehiclePreviousOwnersSelector({
  id,
  value,
  hideCopy = false,
  side = 'left',
  withHotkeyListener = false,
  buttonVariant = 'ghost',
  className,
}: VehiclePreviousOwnersSelectorProps) {
  const previousOwnersButtonRef = useRef<HTMLButtonElement>(null);

  const updatePreviousOwnersMutation = useMutation({
    onMutate: async ({ previousOwners }) => {
      vehicleListingStore.updatePreviousOwners({ id, previousOwners });
    },
    mutationFn: async ({
      previousOwners,
    }: {
      previousOwners: VehicleListing['previous_owners'];
    }) => {
      return await pdkAxios.post(`/inventory/listings/${id}/previous_owners`, {
        previous_owners: previousOwners,
      });
    },
    onError: (error) => {
      toast({
        title: 'Error updating previous owners',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  const handlePreviousOwnersChange = (
    newPreviousOwners: VehicleListing['previous_owners'],
  ) => {
    updatePreviousOwnersMutation.mutate({ previousOwners: newPreviousOwners });
  };

  const currentOption = useMemo(() => {
    return PREVIOUS_OWNERS_OPTIONS_MAP.find(
      (option) => option.value === value.toString(),
    );
  }, [value]);

  return (
    <div>
      <ComboboxPopover
        endAdornment={
          <KeyboardKeyIcon>
            {HOTKEYS.VEHICLE_LISTING_PREVIOUS_OWNERS}
          </KeyboardKeyIcon>
        }
        placeholder="Set previous owners..."
        currentOption={currentOption}
        onChange={(value) => handlePreviousOwnersChange(parseInt(value))}
        emptyMessage="No previous owners found"
        options={PREVIOUS_OWNERS_OPTIONS_MAP}
        side={side}
        align="start"
        onKeyDown={(e) => {
          const keyPressed = e.key;

          const option = PREVIOUS_OWNERS_OPTIONS_MAP.find(
            (option) => option.hotkey === keyPressed,
          );

          if (option) {
            handlePreviousOwnersChange(parseInt(option.value));
            e.preventDefault();
            e.stopPropagation();
          }
        }}
      >
        <div>
          <VehiclePreviousOwnersBadge
            value={value ?? ''}
            hideCopy={hideCopy}
            buttonRef={previousOwnersButtonRef}
            buttonVariant={buttonVariant}
            className={cn('pl-0', className)}
          />
        </div>
      </ComboboxPopover>
      {withHotkeyListener && (
        <HotKeyListener
          hotkeys={HOTKEYS.VEHICLE_LISTING_PREVIOUS_OWNERS}
          onHotkeyPressed={() => {
            previousOwnersButtonRef.current?.click();
          }}
        />
      )}
    </div>
  );
}
